import { useLayoutEffect, useRef, useState } from 'react'

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * A number that rolls to its new value like an odometer, in the direction it moved: up comes in
 * from below, down from above. Old and new share one grid cell for the length of the roll, and
 * the edges fade, so the digits slide out of sight rather than being cut. Decorative: the pill
 * says the value to assistive tech itself.
 */
export const RollingNumber: React.FC<{ value: number }> = ({ value }) => {
  const [roll, setRoll] = useState({ value, old: null as number | null, dir: 0, key: 0 })
  // A new value starts a roll: the one on screen becomes the old one (derived during render).
  if (roll.value !== value) {
    setRoll({ value, old: roll.value, dir: Math.sign(value - roll.value), key: roll.key + 1 })
  }
  const newRef = useRef<HTMLSpanElement>(null)
  const oldRef = useRef<HTMLSpanElement>(null)

  useLayoutEffect(() => {
    if (roll.old === null) return
    const done = () => setRoll((r) => (r.key === roll.key ? { ...r, old: null } : r))
    if (reduceMotion()) {
      done()
      return
    }
    newRef.current?.animate([{ transform: `translateY(${roll.dir * 100}%)` }, { transform: 'none' }], {
      duration: 340,
      easing: 'cubic-bezier(0.2, 0.9, 0.3, 1.25)',
    })
    const out = oldRef.current?.animate(
      [
        { transform: 'none', opacity: 1 },
        { transform: `translateY(${-roll.dir * 100}%)`, opacity: 0 },
      ],
      { duration: 240, easing: 'ease-in', fill: 'forwards' }
    )
    if (out) out.onfinish = done
    else done()
  }, [roll.key]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span
      aria-hidden="true"
      className="inline-grid overflow-hidden align-bottom [mask-image:linear-gradient(transparent,#000_22%,#000_78%,transparent)]"
    >
      {roll.old !== null && (
        <span key={`old-${roll.key}`} ref={oldRef} className="[grid-area:1/1] text-center">
          {roll.old}
        </span>
      )}
      <span key={`new-${roll.key}`} ref={newRef} className="[grid-area:1/1] text-center">
        {roll.value}
      </span>
    </span>
  )
}
